import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Card, TextField, Button, Typography, Avatar, Grid, Alert, Chip, Divider } from '@mui/material';
import PersonIcon from '@mui/icons-material/PersonRounded';
import LockIcon   from '@mui/icons-material/LockRounded';
import { fetchProfile, updateProfile, clearError } from '../../app/slices/profileSlice';
import { PageHeader }    from '../../components/common/PageHeader';
import { LoadingSpinner }from '../../components/common/LoadingSpinner';
import { PasswordField } from '../../components/common/PasswordField';
import { getInitials }   from '../../utils/helpers';

const isPasswordValid = pw =>
  pw.length >= 8 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /[0-9]/.test(pw);

export default function ProfilePage() {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector(s => s.profile);
  const [infoForm, setInfoForm]   = useState({ name: '', phone: '' });
  const [pwdForm, setPwdForm]     = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [saving, setSaving]       = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [success, setSuccess]     = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');
  const [pwdError, setPwdError]   = useState('');

  useEffect(() => { dispatch(fetchProfile()); }, [dispatch]);
  useEffect(() => { if (data) setInfoForm({ name: data.name, phone: data.phone || '' }); }, [data]);

  const handleInfoSave = async () => {
    setSaving(true); setSuccess(''); dispatch(clearError());
    const res = await dispatch(updateProfile({ name: infoForm.name, phone: infoForm.phone }));
    if (res.type.endsWith('/fulfilled')) setSuccess('Profile updated successfully.');
    setSaving(false);
  };

  const handlePwdSave = async () => {
    setPwdError('');
    if (!isPasswordValid(pwdForm.newPassword)) {
      setPwdError('New password does not meet the requirements below.'); return;
    }
    if (pwdForm.newPassword !== pwdForm.confirmPassword) {
      setPwdError('Passwords do not match.'); return;
    }
    setPwdSaving(true);
    const res = await dispatch(updateProfile({
      name: data.name, phone: data.phone,
      currentPassword: pwdForm.currentPassword,
      newPassword: pwdForm.newPassword,
    }));
    if (res.type.endsWith('/fulfilled')) {
      setPwdSuccess('Password changed successfully.');
      setPwdForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } else {
      setPwdError(res.payload || 'Failed to change password.');
    }
    setPwdSaving(false);
  };

  if (loading && !data) return <LoadingSpinner />;

  const roleColor = r => r === 'Admin' ? 'error' : r === 'Manager' ? 'warning' : 'default';

  return (
    <Box>
      <PageHeader title="My Profile" />
      <Grid container spacing={3}>
        {/* Left card — avatar */}
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, textAlign: 'center' }}>
            <Avatar sx={{
              width: 80, height: 80, fontSize: '1.8rem', mx: 'auto', mb: 2,
              background: 'linear-gradient(135deg,#4f46e5,#818cf8)',
              fontWeight: 700, fontFamily: "'Sora',sans-serif",
            }}>
              {getInitials(data?.name)}
            </Avatar>
            <Typography variant="h6" fontWeight={700} fontFamily="'Sora',sans-serif">{data?.name}</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>{data?.email}</Typography>
            <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
              <Chip label={data?.role} size="small" color={roleColor(data?.role)} />
              <Chip label={data?.departmentName} size="small" variant="outlined" />
            </Box>
            {data?.phone && <Typography variant="body2" color="text.secondary" mt={2}>{data.phone}</Typography>}
          </Card>
        </Grid>

        {/* Right — forms */}
        <Grid item xs={12} md={8}>
          {/* Personal Info */}
          <Card sx={{ p: 3, mb: 2.5 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2.5}>
              <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={700} fontFamily="'Sora',sans-serif">Personal Information</Typography>
            </Box>
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
            {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>{error}</Alert>}
            <Box display="flex" flexDirection="column" gap={2.5}>
              <TextField label="Full Name" value={infoForm.name}
                onChange={e => setInfoForm(p => ({ ...p, name: e.target.value }))} fullWidth required />
              <TextField label="Email" value={data?.email || ''} fullWidth disabled
                helperText="Contact admin to change your email address." />
              <TextField label="Login ID" value={data?.loginId || ''} fullWidth disabled
                helperText="Login ID cannot be changed." />
              <TextField label="Phone (optional)" value={infoForm.phone}
                onChange={e => setInfoForm(p => ({ ...p, phone: e.target.value }))}
                fullWidth helperText="e.g. +91 99999 99999" />
              <Box>
                <Button variant="contained" onClick={handleInfoSave}
                  disabled={saving || !infoForm.name}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </Button>
              </Box>
            </Box>
          </Card>

          {/* Change Password */}
          <Card sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2.5}>
              <LockIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={700} fontFamily="'Sora',sans-serif">Change Password</Typography>
            </Box>
            {pwdSuccess && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPwdSuccess('')}>{pwdSuccess}</Alert>}
            {pwdError   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setPwdError('')}>{pwdError}</Alert>}
            <Box display="flex" flexDirection="column" gap={2.5}>
              <PasswordField
                label="Current Password"
                value={pwdForm.currentPassword}
                onChange={e => setPwdForm(p => ({ ...p, currentPassword: e.target.value }))}
                showHints={false}
              />
              <PasswordField
                label="New Password"
                value={pwdForm.newPassword}
                onChange={e => setPwdForm(p => ({ ...p, newPassword: e.target.value }))}
                showHints
              />
              <PasswordField
                label="Confirm New Password"
                value={pwdForm.confirmPassword}
                onChange={e => setPwdForm(p => ({ ...p, confirmPassword: e.target.value }))}
                showHints={false}
              />
              <Box>
                <Button variant="outlined" onClick={handlePwdSave}
                  disabled={pwdSaving || !pwdForm.currentPassword || !pwdForm.newPassword || !pwdForm.confirmPassword}>
                  {pwdSaving ? 'Updating…' : 'Update Password'}
                </Button>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
